import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  BookOpen, 
  FileText, 
  Leaf, 
  TrendingUp,
  ArrowRight,
  Calendar,
  Clock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

gsap.registerPlugin(ScrollTrigger);

const Resources = () => {
  const articlesRef = useRef([]);
  const categoriesRef = useRef([]);

  useEffect(() => {
    // Animate articles
    articlesRef.current.forEach((article, index) => {
      if (article) {
        gsap.from(article, {
          scrollTrigger: {
            trigger: article,
            start: 'top 85%',
          },
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: index * 0.1,
          ease: 'power3.out'
        });
      }
    });

    // Animate categories
    categoriesRef.current.forEach((category, index) => {
      if (category) {
        gsap.from(category, {
          scrollTrigger: {
            trigger: category,
            start: 'top 85%',
          },
          x: -40,
          opacity: 0,
          duration: 0.6,
          delay: index * 0.1,
          ease: 'power2.out'
        });
      }
    });
  }, []);

  const categories = [
    {
      icon: BookOpen,
      title: 'Packaging Basics',
      description: 'Fundamental concepts and best practices for packaging design and selection',
      articleCount: 8
    },
    {
      icon: FileText,
      title: 'Material Selection',
      description: 'Understanding different packaging materials and their applications',
      articleCount: 6
    },
    {
      icon: Leaf,
      title: 'Sustainable Packaging',
      description: 'Eco-friendly options and environmental considerations in packaging',
      articleCount: 5
    },
    {
      icon: TrendingUp,
      title: 'Industry Trends',
      description: 'Latest developments and innovations in packaging technology',
      articleCount: 7
    }
  ];

  const articles = [
    {
      id: 1,
      category: 'Packaging Basics',
      title: 'Understanding Box Construction: A Guide for Startups',
      excerpt: 'Learn about different box styles, construction methods, and how to choose the right option for your product.',
      author: 'Sagar Pack Team',
      date: '2024-01-15',
      readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1716193348749-0be58bc06ad8'
    },
    {
      id: 2,
      category: 'Material Selection',
      title: 'Corrugated vs. Rigid Board: Which is Right for Your Product?',
      excerpt: 'Compare the strengths, costs, and ideal applications of corrugated and rigid board packaging materials.',
      author: 'Sagar Pack Team',
      date: '2024-01-10',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1626253934161-08cfea22e968'
    },
    {
      id: 3,
      category: 'Sustainable Packaging',
      title: 'The Complete Guide to Eco-Friendly Packaging Materials',
      excerpt: 'Explore sustainable alternatives, recyclable materials, and how to reduce your packaging environmental impact.',
      author: 'Sagar Pack Team',
      date: '2024-01-05',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1755606277170-4e89d5847e14'
    },
    {
      id: 4,
      category: 'Industry Trends',
      title: 'E-commerce Packaging Trends for 2024',
      excerpt: 'Discover the latest trends in e-commerce packaging including unboxing experiences and sustainable solutions.',
      author: 'Sagar Pack Team',
      date: '2023-12-28',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1716540103530-cc33cdd20cde'
    },
    {
      id: 5,
      category: 'Packaging Basics',
      title: 'How to Calculate MOQ for Custom Packaging',
      excerpt: 'Understanding minimum order quantities, cost factors, and how to plan your packaging production budget.',
      author: 'Sagar Pack Team',
      date: '2023-12-20',
      readTime: '5 min read',
      image: 'https://images.pexels.com/photos/5025503/pexels-photo-5025503.jpeg'
    },
    {
      id: 6,
      category: 'Material Selection',
      title: 'Understanding Kraft Paper: Properties and Applications',
      excerpt: 'Learn about kraft paper characteristics, grades, and when to use it for your packaging needs.',
      author: 'Sagar Pack Team',
      date: '2023-12-15',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1661347232532-9376b989b51c'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Knowledge Base</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
              Packaging
              <br />
              <span className="text-gray-600">Insights</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Stay informed with expert knowledge, practical guides, and industry insights designed 
              to help businesses make better packaging decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Explore by Category
            </h2>
            <p className="text-lg text-gray-600">
              Browse articles organized by topic to find the information you need
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card
                  key={index}
                  ref={el => categoriesRef.current[index] = el}
                  className="group border-2 hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <CardContent className="p-6 space-y-4 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-navy-900 rounded-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-navy-900">{category.title}</h3>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                    <p className="text-sm text-gray-500">{category.articleCount} articles</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Latest Articles
            </h2>
            <p className="text-lg text-gray-600">
              Recent insights and guides from our packaging experts
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Card
                key={article.id}
                ref={el => articlesRef.current[index] = el}
                className="group overflow-hidden border-2 hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/30 to-transparent opacity-50"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-white text-navy-900 px-3 py-1 rounded-full text-xs font-semibold">
                      {article.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold text-navy-900 group-hover:text-gray-700 transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 pt-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white mt-4"
                  >
                    Read Article <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">More articles coming soon</p>
            <Button
              variant="outline"
              className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white"
            >
              Subscribe for Updates
            </Button>
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-24 bg-navy-900 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need Personalized Guidance?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Our packaging experts are available to provide specific advice for your project requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-navy-900 hover:bg-gray-100 text-lg px-8 py-6"
              asChild
            >
              <Link to="/contact">
                Contact Our Team <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-navy-900 text-lg px-8 py-6"
              asChild
            >
              <a href="tel:+918879049450">Call +91 88790 49450</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
